import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompaniesService } from '@core/api/companies.service';
import { DigitalizationService } from '@core/api/digitalization.service';
import { ProductsService } from '@core/api/products.service';
import { AssignPlanToCompanyRequest } from '@core/model/assignPlanToCompanyRequest';
import { MediaMetricsBySourceTypeResponse } from '@core/model/mediaMetricsBySourceTypeResponse';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AiAgent, AiAgentsService } from './ai-agents.service';

export interface CompanyLimitUsageValue {
  value: number;
  available: boolean;
  limit?: number;
}

export interface CompanyPlanUsage {
  users: CompanyLimitUsageValue;
  products: CompanyLimitUsageValue;
  digitalizations: CompanyLimitUsageValue;
  storage: CompanyLimitUsageValue;
  agents: AiAgent[];
  agentsAvailable: boolean;
}

/**
 * Frontend orchestrator for company plan limits.
 *
 * Each read/write goes through the gateway directly to the service that owns the
 * data. No backend service needs to call another backend service.
 */
@Injectable({ providedIn: 'root' })
export class CompanyPlanLimitsService {
  private readonly mediaBaseUrl = `${environment.API_BASE_PATH}/v1/Media/api`;

  constructor(
    private readonly http: HttpClient,
    private readonly companiesService: CompaniesService,
    private readonly productsService: ProductsService,
    private readonly digitalizationService: DigitalizationService,
    private readonly aiAgentsService: AiAgentsService
  ) { }

  getUsage(companyId: string, langCode: string): Observable<CompanyPlanUsage> {
    const mediaParams = new HttpParams().set('companyId', companyId);

    return forkJoin({
      users: this.asUsage(this.companiesService.companiesGetAll(), (users) => users?.length ?? 0),
      products: this.asUsage(
        this.productsService.productsPaginatedProducts(langCode, 1, ''),
        (response) => response?.totalCount ?? 0
      ),
      digitalizations: this.asUsage(
        this.digitalizationService.digitalizationPaginatedGetDocuments(langCode, 1, ''),
        (response) => response?.totalCount ?? 0
      ),
      storage: this.http.get<MediaMetricsBySourceTypeResponse>(`${this.mediaBaseUrl}/Metrics`, { params: mediaParams })
        .pipe(
          map((response) => ({
            value: this.megabytesToGigabytes(response?.totalSizeInMB ?? 0),
            limit: this.megabytesToGigabytes(response?.limitInMB ?? 0),
            available: true
          })),
          catchError(() => of({ value: 0, available: false }))
        ),
      agents: this.aiAgentsService.getCompanyCatalog().pipe(
        map((agents) => ({ agents: agents ?? [], available: true })),
        catchError(() => of({ agents: [] as AiAgent[], available: false }))
      )
    }).pipe(
      map(({ users, products, digitalizations, storage, agents }) => ({
        users,
        products,
        digitalizations,
        storage,
        agents: agents.agents,
        agentsAvailable: agents.available
      }))
    );
  }

  /**
   * Persists the company limits, ensures that Media owns an account/profile for
   * the company and then updates the storage limit.
   */
  saveConfiguration(
    companyId: string,
    accountName: string,
    request: AssignPlanToCompanyRequest,
    storageInGigabytes: number
  ): Observable<void> {
    return this.companiesService
      .v1CompaniesApiCompaniesCompanyIdPlanPost(companyId, request)
      .pipe(
        switchMap(() => this.http.post<string>(
          `${this.mediaBaseUrl}/MediaAccount`,
          { companyId, accountName }
        )),
        switchMap(() => this.http.patch<void>(
          `${this.mediaBaseUrl}/MediaAccount/${encodeURIComponent(companyId)}/storage`,
          { storage: storageInGigabytes }
        ))
      );
  }

  private asUsage<T>(source: Observable<T>, project: (value: T) => number): Observable<CompanyLimitUsageValue> {
    return source.pipe(
      map((value) => ({ value: project(value), available: true })),
      catchError(() => of({ value: 0, available: false }))
    );
  }

  private megabytesToGigabytes(megabytes: number): number {
    return Math.round((megabytes / 1024) * 100) / 100;
  }
}
